import { useState, useEffect, useContext, useRef } from 'react';
import { debounce } from 'lodash-es';
import { ReactFlow, useReactFlow, useOnViewportChange } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { Wrapper } from '@klv/utils/wrapper';

import { FlowsFormSlideContext } from '../context/flowsFormSlide';
import * as s from './styles/flows.module.scss';
import { FlowForm } from './flowForm';
import { FlowNode } from './flowNode';
import { FlowEdge } from './flowEdge';
import { FlowToolbar } from './flowToolbar';
import { nodes, edges } from '../data';

import type { TFlow } from '../types';
import backgroundImage from '../assets/clouds.webp';


export const FlowsAI = ({ setPage }: { setPage: (arg: string) => void; }) => {
  
  const flowContainer = useRef<HTMLDivElement>(null);
  const { slide } = useContext(FlowsFormSlideContext);

  const [currentZoom, setCurrentZoom] = useState(1);
  const [flow, setFlow] = useState<TFlow | null>(null);
  const nodeSet = flow ? nodes?.[flow] : [];
  const edgeSet = flow ? edges?.[flow] : [];

  const { zoomIn, zoomOut, getZoom, fitView } = useReactFlow();
  
  useOnViewportChange({
    onEnd: () => setCurrentZoom(getZoom()),
  });
  
  useEffect(() => {
    // automaticallt fit the view when the slide, or flow changes
    fitView();
    
    const handleResize = debounce(() => {
      fitView();
    }, 10);

    // fit the view when the window resizes
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [flow, fitView, flowContainer, slide]);

  return (
    <Wrapper
      customPadding={{ customPaddingBottom: 'none', customPaddingTop: 'none' }}
      className={s.flowsAiFlowWrapper}
      style={{ backgroundImage: `url(${backgroundImage})` }}
      contained
      grid
    >
      <FlowForm flow={flow} setFlow={setFlow} setPage={setPage} />
      {flow && (
        <div className={s.flow} ref={flowContainer}>
          <ReactFlow
            nodes={nodeSet.map((node) => {
              // Determine if the node should show information
              if (node?.data?.showInformation !== undefined) {
                node.data.showInformation = slide >= 3;
              }
              return node;
            })}
            edges={edgeSet}
            nodeTypes={{ custom: FlowNode }}
            edgeTypes={{ custom: FlowEdge }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.4}
            fitView={true}
          />
          <FlowToolbar zoomIn={zoomIn} zoomOut={zoomOut} zoom={currentZoom} fitView={fitView}/>
        </div>
      )}
    </Wrapper>
  );
};
